import React, { useState, useEffect } from 'react';
import { fetchTokenPrice, fetchProtocolApy, fetchTotalValueLocked, fetchTokenVolume, fetchTokenMarketData } from '../data/defi';

const DeFiMetricsCard = () => {
  const [metrics, setMetrics] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedToken, setSelectedToken] = useState('ethereum');

  const tokens = [
    { id: 'ethereum', name: 'Ethereum', symbol: 'ETH', icon: 'fab fa-ethereum', hasStaking: true, hasDeFi: true },
    { id: 'bitcoin', name: 'Bitcoin', symbol: 'BTC', icon: 'fab fa-bitcoin', hasStaking: false, hasDeFi: false },
    { id: 'binancecoin', name: 'BNB', symbol: 'BNB', icon: 'fas fa-coins', hasStaking: true, hasDeFi: true },
    { id: 'ripple', name: 'XRP', symbol: 'XRP', icon: 'fas fa-bolt', hasStaking: false, hasDeFi: false },
    { id: 'solana', name: 'Solana', symbol: 'SOL', icon: 'fas fa-sun', hasStaking: true, hasDeFi: true },
    { id: 'cardano', name: 'Cardano', symbol: 'ADA', icon: 'fas fa-shapes', hasStaking: true, hasDeFi: true },
    { id: 'polkadot', name: 'Polkadot', symbol: 'DOT', icon: 'fas fa-circle', hasStaking: true, hasDeFi: true },
    { id: 'dogecoin', name: 'Dogecoin', symbol: 'DOGE', icon: 'fas fa-dog', hasStaking: false, hasDeFi: false }
  ];

  useEffect(() => {
    const loadMetrics = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const [priceData, apy, tvl, volumeData, marketData] = await Promise.all([
          fetchTokenPrice(selectedToken).catch(err => {
            console.warn(`Price fetch failed for ${selectedToken}:`, err);
            return null;
          }),
          fetchProtocolApy(selectedToken).catch(err => {
            console.warn(`APY fetch failed for ${selectedToken}:`, err);
            return null;
          }),
          fetchTotalValueLocked(selectedToken).catch(err => {
            console.warn(`TVL fetch failed for ${selectedToken}:`, err);
            return null;
          }),
          fetchTokenVolume(selectedToken).catch(err => {
            console.warn(`Volume fetch failed for ${selectedToken}:`, err);
            return 0;
          }),
          fetchTokenMarketData(selectedToken).catch(err => {
            console.warn(`Market data fetch failed for ${selectedToken}:`, err);
            return null;
          })
        ]);

        // If we can't get price data, that's a critical error
        if (!priceData) {
          throw new Error(`Unable to load data for ${selectedToken}. Please try again.`);
        }

        setMetrics({
          price: priceData?.usd || 0,
          priceChange: priceData?.usd_24h_change || 0,
          avgApy: apy,
          totalValueLocked: tvl,
          volume24h: volumeData || 0,
          marketCap: marketData?.market_cap?.usd || 0,
          marketCapChange: marketData?.market_cap_change_percentage_24h || 0
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadMetrics();
  }, [selectedToken]);

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: value < 1 ? 4 : 2
    }).format(value);
  };

  const formatPercentage = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return `${value > 0 ? '+' : ''}${value?.toFixed(2)}%`;
  };

  const getTokenIcon = () => {
    const token = tokens.find(t => t.id === selectedToken);
    return token?.icon || 'fas fa-coins';
  };

  const getTokenSymbol = () => {
    const token = tokens.find(t => t.id === selectedToken);
    return token?.symbol || 'TOKEN';
  };

  const getCurrentToken = () => {
    return tokens.find(t => t.id === selectedToken);
  };

  const handleRetry = () => {
    setError(null);
    setLoading(true);
    // Reload metrics
    useEffect(() => {
      const loadMetrics = async () => {
        try {
          setLoading(true);
          setError(null);
          
          const [priceData, apy, tvl, volumeData, marketData] = await Promise.all([
            fetchTokenPrice(selectedToken).catch(err => {
              console.warn(`Price fetch failed for ${selectedToken}:`, err);
              return null;
            }),
            fetchProtocolApy(selectedToken).catch(err => {
              console.warn(`APY fetch failed for ${selectedToken}:`, err);
              return null;
            }),
            fetchTotalValueLocked(selectedToken).catch(err => {
              console.warn(`TVL fetch failed for ${selectedToken}:`, err);
              return null;
            }),
            fetchTokenVolume(selectedToken).catch(err => {
              console.warn(`Volume fetch failed for ${selectedToken}:`, err);
              return 0;
            }),
            fetchTokenMarketData(selectedToken).catch(err => {
              console.warn(`Market data fetch failed for ${selectedToken}:`, err);
              return null;
            })
          ]);

          if (!priceData) {
            throw new Error(`Unable to load data for ${selectedToken}. Please try again.`);
          }

          setMetrics({
            price: priceData?.usd || 0,
            priceChange: priceData?.usd_24h_change || 0,
            avgApy: apy,
            totalValueLocked: tvl,
            volume24h: volumeData || 0,
            marketCap: marketData?.market_cap?.usd || 0,
            marketCapChange: marketData?.market_cap_change_percentage_24h || 0
          });
        } catch (err) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      };

      loadMetrics();
    }, [selectedToken]);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-6 shadow-lg">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 gap-4">
          <h2 className="text-2xl font-bold text-dominant">DeFi Metrics</h2>
          
          <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
            <select 
              value={selectedToken}
              onChange={(e) => setSelectedToken(e.target.value)}
              className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-secondary bg-white"
              disabled={loading}
            >
              {tokens.map(token => (
                <option key={token.id} value={token.id}>
                  {token.name} ({token.symbol})
                </option>
              ))}
            </select>
            
            <div className="text-sm text-gray-500 bg-gray-50 px-3 py-2 rounded-lg flex items-center gap-2">
              <i className={getTokenIcon()}></i>
              <span>{getTokenSymbol()}</span>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="animate-pulse bg-gray-100 rounded-lg p-6">
              <div className="h-6 bg-gray-200 rounded w-3/4 mx-auto mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl p-6 shadow-lg">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 gap-4">
          <h2 className="text-2xl font-bold text-dominant">DeFi Metrics</h2>
          
          <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
            <select 
              value={selectedToken}
              onChange={(e) => setSelectedToken(e.target.value)}
              className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-secondary bg-white"
            >
              {tokens.map(token => (
                <option key={token.id} value={token.id}>
                  {token.name} ({token.symbol})
                </option>
              ))}
            </select>
            
            <div className="text-sm text-gray-500 bg-gray-50 px-3 py-2 rounded-lg flex items-center gap-2">
              <i className={getTokenIcon()}></i>
              <span>{getTokenSymbol()}</span>
            </div>
          </div>
        </div>
        
        <div className="text-center text-red-600 py-8">
          <i className="fas fa-exclamation-triangle text-3xl mb-3"></i>
          <p className="text-lg font-medium mb-2">Failed to load DeFi metrics</p>
          <p className="text-sm text-gray-600 mb-4">{error}</p>
          <button 
            onClick={handleRetry}
            className="bg-secondary text-white px-6 py-2 rounded-lg hover:bg-dominant transition-colors"
          >
            <i className="fas fa-refresh mr-2"></i>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const currentToken = getCurrentToken();
  
  const metricCards = [
    {
      label: `${getTokenSymbol()} Price`,
      value: formatCurrency(metrics.price),
      change: metrics.priceChange,
      positive: metrics.priceChange > 0,
      icon: getTokenIcon(),
      available: true
    },
    {
      label: 'Market Cap',
      value: formatCurrency(metrics.marketCap),
      change: metrics.marketCapChange,
      positive: metrics.marketCapChange > 0,
      icon: 'fas fa-chart-bar',
      available: true
    },
    {
      label: '24h Volume',
      value: formatCurrency(metrics.volume24h),
      change: metrics.priceChange,
      positive: metrics.priceChange > 0,
      icon: 'fas fa-exchange-alt',
      available: true
    },
    {
      label: currentToken?.hasDeFi ? 'DeFi TVL' : 'DeFi Ecosystem',
      value: currentToken?.hasDeFi ? formatCurrency(metrics.totalValueLocked) : 'No DeFi',
      change: currentToken?.hasDeFi ? 2.1 : null,
      positive: currentToken?.hasDeFi,
      icon: 'fas fa-lock',
      available: currentToken?.hasDeFi
    }
  ];

  if (currentToken?.hasStaking) {
    metricCards.splice(3, 0, {
      label: 'Avg APY',
      value: metrics.avgApy !== null ? `${metrics.avgApy?.toFixed(2)}%` : 'Loading...',
      change: metrics.avgApy !== null ? 0.1 : null,
      positive: true,
      icon: 'fas fa-percentage',
      available: metrics.avgApy !== null
    });
  }

  return (
    <div className="bg-white rounded-xl p-6 shadow-lg">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 gap-4">
        <h2 className="text-2xl font-bold text-dominant">DeFi Metrics</h2>
        
        <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
          <select 
            value={selectedToken}
            onChange={(e) => setSelectedToken(e.target.value)}
            className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-secondary bg-white"
          >
            {tokens.map(token => (
              <option key={token.id} value={token.id}>
                {token.name} ({token.symbol})
              </option>
            ))}
          </select>
          
          <div className="text-sm text-gray-500 bg-gray-50 px-3 py-2 rounded-lg flex items-center gap-2">
            <i className={getTokenIcon()}></i>
            <span>{getTokenSymbol()}</span>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {metricCards.map((metric, index) => (
          <div 
            key={index} 
            className={`bg-gradient-to-br rounded-lg p-4 border transition-colors ${
              metric.available 
                ? 'from-gray-50 to-white border-gray-200 hover:border-secondary' 
                : 'from-gray-100 to-gray-200 border-gray-300 opacity-75'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                metric.available ? 'bg-secondary' : 'bg-gray-400'
              }`}>
                <i className={`${metric.icon} text-white text-sm`}></i>
              </div>
              {metric.available && metric.change !== null && (
                <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                  metric.positive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {formatPercentage(metric.change)}
                </span>
              )}
            </div>
            
            <div className={`font-bold mb-1 truncate ${
              metric.available ? 'text-dominant text-lg' : 'text-gray-500 text-md'
            }`} title={metric.value}>
              {metric.value}
            </div>
            <div className="text-xs text-gray-600 truncate">{metric.label}</div>
            
            {!metric.available && (
              <div className="mt-2 text-xs text-gray-500 italic">
                Not applicable
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Token Info */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Selected Token:</span>
            <span className="font-medium flex items-center gap-2">
              <i className={getTokenIcon()}></i>
              {tokens.find(t => t.id === selectedToken)?.name}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Staking Available:</span>
            <span className={`font-medium ${currentToken?.hasStaking ? 'text-green-600' : 'text-gray-500'}`}>
              {currentToken?.hasStaking ? 'Yes' : 'No'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">DeFi Ecosystem:</span>
            <span className={`font-medium ${currentToken?.hasDeFi ? 'text-green-600' : 'text-gray-500'}`}>
              {currentToken?.hasDeFi ? 'Yes' : 'No'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">24h Change:</span>
            <span className={`font-medium ${metrics.priceChange > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatPercentage(metrics.priceChange)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeFiMetricsCard;